// src/pages/Login.tsx
import React from "react";
import { supabase } from "../lib/supabaseClient";
import logo from "../assets/logocompleto.png";
import loginQR from "../assets/login-qr.png";

/** Iconos SVG (mail/lock/eye) */
const MailIcon = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}>
    <path d="M4 6h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z"/>
    <path d="m4 7 8 6 8-6"/>
  </svg>
);
const LockIcon = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}>
    <rect x="4" y="10" width="16" height="10" rx="2"/>
    <path d="M8 10V7a4 4 0 1 1 8 0v3"/>
  </svg>
);
const EyeIcon = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}>
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}>
    <path d="M3 3l18 18"/>
    <path d="M10.6 10.6a3 3 0 0 0 4.25 4.25"/>
    <path d="M9.88 4.24A9.77 9.77 0 0 1 12 4c6.5 0 10 6 10 6a17.2 17.2 0 0 1-3.07 3.73M6.2 6.2C3.84 7.94 2 10 2 10s3.5 6 10 6c1.2 0 2.35-.2 3.43-.56"/>
  </svg>
);

export default function Login() {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const em = email.trim().toLowerCase();
    if (!em || !password) {
      setError("Completá email y contraseña.");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: em,
        password,
      });
      // Log de diagnóstico en consola para cualquiera de los dos casos
      console.log("signInWithPassword result:", { data, error });

      if (error) {
        // Mensajes comunes
        const code = error.code || "";
        let msg = error.message || "Error al iniciar sesión.";
        if (code === "invalid_credentials" || /Invalid login/i.test(msg)) {
          msg = "Credenciales inválidas. Verificá email y contraseña.";
        } else if (code === "email_not_confirmed") {
          msg = "Email no confirmado. Pedí confirmación al administrador.";
        }
        setError(`${msg}${error.status ? ` (status ${error.status})` : ""}`);
        return;
      }

      if (!data?.user) {
        setError("No se pudo iniciar sesión (usuario no devuelto).");
        return;
      }

      // redirigí a tu ruta protegida
      window.location.assign("/admin");
    } catch (err: any) {
      console.error("Auth exception:", err);
      setError(err?.message || "Error de autenticación.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        <div className="auth-brand" style={{ justifyContent: "center" }}>
          <img className="auth-logo" src={logo} alt="PreciosQR" />
          <img className="auth-qr" src={loginQR} alt="QR de acceso" loading="lazy" />
        </div>

        <form onSubmit={onSubmit} noValidate>
          <div className="field">
            <MailIcon className="field-icon" />
            <input
              className="input field-input"
              type="email"
              placeholder="tu@correo.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              aria-label="Correo electrónico"
            />
          </div>

          <div className="field">
            <LockIcon className="field-icon" />
            <input
              className="input field-input"
              type={showPw ? "text" : "password"}
              placeholder="Contraseña"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-label="Contraseña"
            />
            <button
              type="button"
              className="pw-toggle"
              onClick={() => setShowPw((v) => !v)}
              aria-pressed={showPw}
              title={showPw ? "Ocultar" : "Mostrar"}
            >
              {showPw ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>

          {error && <div className="alert-error" style={{ marginBottom: 10 }}>{error}</div>}

          <button className="btn auth-submit" type="submit" disabled={loading}>
            {loading ? "Ingresando…" : "Entrar"}
          </button>

          <p className="auth-note">
            Acceso exclusivo para personal autorizado de <strong>Farmacia Del Puente</strong>.
          </p>
        </form>
      </div>
    </div>
  );
}
